import { jwtDecode } from 'jwt-decode';
import { tokenStorageOptions } from './config';
import { generateRandomString, generateCodeChallenge } from './pkce';

const codeVerifierStorageKey = 'PKCE_code_verifier'
const stateStorageKey = 'ROCP_auth_state'
const tokenResponseStorageKey = 'token_response'

export const authenticatePkce = async (config) => {
    const codeResponse = await openLogin(config);
    const tokenResponse = await fetchTokens(codeResponse, config);
    const storage = getConfiguredStorage();
    storage.setItem(tokenResponseStorageKey, JSON.stringify(tokenResponse))
    return tokenResponse;
}

export const authenticateCodePkce = async (config) => {
    return await openLogin(config);
}

export const getTokenFromStorage = () => {
    const storage = getConfiguredStorage();
    const storageTokenResponse = storage.getItem(tokenResponseStorageKey);

    // console.log('storageTokenResponse', storageTokenResponse);
    return storageTokenResponse ? JSON.parse(storageTokenResponse) : null;

}

export const verifyToken = (tokenResponse) => {
    if (!tokenResponse?.idToken) {
        return false;
    }
    const decoded = jwtDecode(tokenResponse?.idToken);
    const exp = decoded.exp * 1000 - (1000 * 60 * 2); // convert from s to ms, then remove 2 minutes     
    const tokenHasExpired = new Date().getTime() > exp;
    return !tokenHasExpired;
}
export const clearToken = () => {
    const storage = getConfiguredStorage();
    storage.removeItem(tokenResponseStorageKey);
}

const getConfiguredStorage = () => {
    //Default to local
    let storage = localStorage;
    if (tokenStorageOptions.location === 'local_storage') {
        storage = localStorage
    } else if (tokenStorageOptions.location === 'session_storage') {
        storage = sessionStorage
    }

    return storage;
}

async function openLogin(config) {
    const codeVerifier = generateRandomString(96)
    sessionStorage.setItem(codeVerifierStorageKey, codeVerifier);

    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = "20123.22233";
    // Set query parameters and redirect user to OAuth2 authentication endpoint
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: config.auth.clientId,
        redirect_uri: config.auth.redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        scope: config.auth.scopes,
        prompt: 'select_account',
        response_mode: 'query'
    });

    sessionStorage.removeItem(stateStorageKey)

    if (state) {
        sessionStorage.setItem(stateStorageKey, state)
        params.append('state', state)
    }
    const authorizeEndpoint = `${config.auth.authority}/oauth2/v2.0/authorize`;
    const url = `${authorizeEndpoint}?${params.toString()}`;

    const popup = window.open(url, "popup", `popup=true,height=${config.size?.height ? config.size?.height : 570},width=${config.size?.width ? config.size?.width : 520}`);

    const codePromise = new Promise((resolve) => {
        const checkPopup = setInterval(() => {
            const currentOrigin = window.location.origin;
            let origin;
            let search;
            let searchParams;

            try {
                origin = popup.window.location.origin;
                search = popup.window.location.search;
                console.log(popup.window.location)
                searchParams = new URLSearchParams(search);
            } catch (err) {
                // SecurityError: Blocked a frame with origin from accessing a cross-origin frame
                // same-origin policy
                // console.log(err)
            }

            if (currentOrigin === origin && searchParams?.has("error")) {
                clearInterval(checkPopup);
            }

            if (currentOrigin === origin) {
                popup.close()
                handleCodeResponse(search, config, resolve);
                clearInterval(checkPopup);
            }
            
            if (!popup || !popup.closed) return;
        }, 500);

    })

    const codeResponse = await codePromise;
    return codeResponse;
}

function handleCodeResponse(search, config, callback) {
    search = search.slice(1)
    const urlParams = new URLSearchParams(search);
    validateState(urlParams);

    const authCode = urlParams.get('code');
    if (!authCode) {
        throw Error("Parameter 'code' not found in URL. \nHas authentication taken place?")
    }

    const codeVerifier = window.sessionStorage.getItem(codeVerifierStorageKey);
    if (!codeVerifier) {
        throw Error("Can't get tokens without the CodeVerifier. \nHas authentication taken place?")
    }

    callback({
        authorizationCode: authCode,
        codeVerifier: codeVerifier,
    });
}

async function fetchTokens(codeResponse, config) {
    const tokenEndpoint = "https://login.microsoftonline.com/common/oauth2/v2.0/token";
    const tokenRequest = {
        grant_type: 'authorization_code',
        code: codeResponse.authorizationCode,
        client_id: config.auth.clientId,
        redirect_uri: config.auth.redirectUri,
        code_verifier: codeResponse.codeVerifier,
    }

    const tokenResponse = postTokenRequest(tokenEndpoint, tokenRequest);
    return tokenResponse;

}

function postTokenRequest(tokenEndpoint, tokenRequest) {
    return postWithXForm(tokenEndpoint, tokenRequest).then((response) => {
        return response.json().then((body) => {
            if (isTokenResponse(body)) {
                const tokenResponse = {
                    accessToken: body.access_token,
                    refreshToken: body.refresh_token,
                    idToken: body.id_token,
                    tokenType: body.token_type,
                    expiresIn: body.expires_in,
                    scope: body.scope,
                }
                return tokenResponse;
            }
            throw Error(JSON.stringify(body))
        })
    })
}

function isTokenResponse(body) {
    return (body).access_token !== undefined;
}

function validateState(urlParams) {
    const receivedState = urlParams.get('state');
    const loadedState = sessionStorage.getItem(stateStorageKey);
    if (receivedState !== loadedState) {
        throw new Error(
            '"state" value received from authentication server does no match client request. Possible cross-site request forgery'
        );
    }
}

function buildUrlEncodedRequest(request) {
    let queryString = '';
    for (const [key, value] of Object.entries(request)) {
        queryString += `${queryString ? '&' : ''}${key}=${encodeURIComponent(value)}`;
    }
    return queryString;
}

async function postWithXForm(url, request) {
    return fetch(url, {
        method: 'POST',
        body: buildUrlEncodedRequest(request),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).then(async (response) => {
        if (!response.ok) {
            const responseBody = await response.text();
            throw new Error(responseBody);
        }
        return response;
    })
}